class ScienceV3 extends HTMLElement {
  connectedCallback() {
    ScienceV3.instances = ScienceV3.instances || new Set();
    ScienceV3.instances.add(this);

    this.tabs = Array.from(this.querySelectorAll('[data-sv3-tab]'));
    this.textSlides = Array.from(this.querySelectorAll('[data-sv3-text-slide]'));
    this.mediaSlides = Array.from(this.querySelectorAll('[data-sv3-media-slide]'));
    this.mobileTabsNav = this.querySelector('.science-v3__tabs--mobile');

    this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
    this.autoplayMs = Math.max(1000, parseInt(this.getAttribute('data-autoplay-ms') || '4000', 10));
    this.pauseOnInteraction = this.getAttribute('data-pause-on-interaction') !== 'false';

    this.userInteracted = false;
    this.currentIndex = 0;
    this.totalSlides = Math.max(this.textSlides.length, this.mediaSlides.length);
    this.autoplayInterval = null;
    this.hasEnteredView = false;
    this.intersectionObserver = null;

    this.onTabClick = this.onTabClick.bind(this);
    this.onIntersect = this.onIntersect.bind(this);

    ScienceV3.ensureMobileTabsScheduler();

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));

    this.switchTo('0', { animateNumber: false, forceAnimate: false });
    this.setupIntersectionObserver();
    ScienceV3.requestMobileTabsRefresh();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.tabs.forEach((tab) => tab.removeEventListener('click', this.onTabClick));

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    if (this.mobileTabsNav) {
      this.mobileTabsNav.classList.remove('visible');
    }

    if (ScienceV3.instances) {
      ScienceV3.instances.delete(this);
      ScienceV3.requestMobileTabsRefresh();
    }
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      this.hasEnteredView = true;
      this.animateActiveStat(String(this.currentIndex));
      if (this.autoplayEnabled && this.totalSlides > 1) this.startAutoplay();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(this.onIntersect, {
      threshold: 0.2,
    });

    this.intersectionObserver.observe(this);
  }

  static ensureMobileTabsScheduler() {
    if (ScienceV3._mobileTabsSchedulerBound) return;
    ScienceV3._mobileTabsSchedulerBound = true;

    const schedule = () => ScienceV3.requestMobileTabsRefresh();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
  }

  static requestMobileTabsRefresh() {
    if (ScienceV3._mobileTabsRafId) return;
    ScienceV3._mobileTabsRafId = window.requestAnimationFrame(() => {
      ScienceV3._mobileTabsRafId = null;
      ScienceV3.refreshMobileTabs();
    });
  }

  static refreshMobileTabs() {
    const instances = Array.from(ScienceV3.instances || []);
    if (!instances.length) return;

    const isMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) {
      instances.forEach((inst) => inst.mobileTabsNav && inst.mobileTabsNav.classList.remove('visible'));
      return;
    }

    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
    const minVisiblePx = 24;
    const viewportCenter = viewportH / 2;

    let winner = null;
    let bestScore = Infinity;

    instances.forEach((inst) => {
      if (!inst.mobileTabsNav) return;
      if (!viewportH) return;

      const rect = inst.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, viewportH);
      const visiblePx = Math.max(0, visibleBottom - visibleTop);

      if (visiblePx < minVisiblePx) return;

      const sectionCenter = (rect.top + rect.bottom) / 2;
      const score = Math.abs(sectionCenter - viewportCenter);
      if (score < bestScore) {
        bestScore = score;
        winner = inst;
      }
    });

    instances.forEach((inst) => {
      if (!inst.mobileTabsNav) return;
      inst.mobileTabsNav.classList.toggle('visible', inst === winner);
    });
  }

  onIntersect(entries) {
    if (this.hasEnteredView) return;
    const entry = entries && entries[0];
    if (!entry || !entry.isIntersecting) return;

    this.hasEnteredView = true;
    this.animateActiveStat(String(this.currentIndex));

    if (this.autoplayEnabled && this.totalSlides > 1) {
      this.startAutoplay();
    }
  }

  onTabClick(event) {
    const tab = event.currentTarget;
    const index = tab.getAttribute('data-sv3-tab');
    if (index == null) return;

    if (this.pauseOnInteraction) {
      this.userInteracted = true;
      this.stopAutoplay();
    }

    this.switchTo(String(index), { animateNumber: true, forceAnimate: false });
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled) return;
    if (this.totalSlides <= 1) return;
    if (this.userInteracted && this.pauseOnInteraction) return;
    if (!this.hasEnteredView) return;

    this.autoplayInterval = setInterval(() => {
      if (this.userInteracted && this.pauseOnInteraction) {
        this.stopAutoplay();
        return;
      }
      const nextIndex = (this.currentIndex + 1) % this.totalSlides;
      this.switchTo(String(nextIndex), { animateNumber: true, forceAnimate: false });
    }, this.autoplayMs);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  switchTo(index, { animateNumber, forceAnimate } = {}) {
    const prevIndex = String(this.currentIndex);
    this.currentIndex = parseInt(index, 10) || 0;
    const activeIndex = String(this.currentIndex);

    this.tabs.forEach((t) => t.classList.toggle('is-active', t.getAttribute('data-sv3-tab') === activeIndex));
    this.textSlides.forEach((s) => s.classList.toggle('is-active', s.getAttribute('data-sv3-text-slide') === activeIndex));
    this.mediaSlides.forEach((s) => s.classList.toggle('is-active', s.getAttribute('data-sv3-media-slide') === activeIndex));

    if (animateNumber && this.hasEnteredView && (forceAnimate || activeIndex !== prevIndex)) {
      this.animateActiveStat(activeIndex);
    }
  }

  animateActiveStat(index) {
    const activeMedia = this.mediaSlides.find((s) => s.getAttribute('data-sv3-media-slide') === index);
    if (!activeMedia) return;

    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isDesktop = window.matchMedia && window.matchMedia('(min-width: 768px)').matches;

    let targets = [];
    if (isDesktop) {
      targets = Array.from(activeMedia.querySelectorAll('.science-v3__overlay--stat [data-sv3-count-target]'));
    } else {
      targets = Array.from(activeMedia.querySelectorAll('.science-v3__stat-card--mobile [data-sv3-count-target]'));
    }
    if (!targets.length) {
      targets = Array.from(activeMedia.querySelectorAll('[data-sv3-count-target]'));
    }
    if (!targets.length) return;

    targets.forEach((el) => this.animateCountElement(el, prefersReducedMotion));
  }

  animateCountElement(el, prefersReducedMotion) {
    const raw = el.getAttribute('data-sv3-count-target') || '';
    const match = raw.match(/(\d+(?:\.\d+)?)/);
    if (!match) return;

    const target = parseFloat(match[1]);
    const prefix = raw.substring(0, raw.indexOf(match[1]));
    const suffix = raw.substring(raw.indexOf(match[1]) + match[1].length);
    const isDecimal = match[1].includes('.');

    if (prefersReducedMotion) {
      el.textContent = prefix + match[1] + suffix;
      return;
    }

    if (el._sv3RafId) {
      window.cancelAnimationFrame(el._sv3RafId);
      el._sv3RafId = null;
    }

    const start = 0;
    const duration = 900;
    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (target - start) * eased;

      if (isDecimal) {
        el.textContent = prefix + current.toFixed(1) + suffix;
      } else {
        el.textContent = prefix + Math.round(current) + suffix;
      }

      if (progress < 1) {
        el._sv3RafId = window.requestAnimationFrame(step);
      } else {
        el._sv3RafId = null;
      }
    };

    el.textContent = prefix + (isDecimal ? start.toFixed(1) : Math.round(start)) + suffix;
    el._sv3RafId = window.requestAnimationFrame(step);
  }
}

if (!customElements.get('science-v3')) {
  customElements.define('science-v3', ScienceV3);
}
